import { useState, useRef, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase, uploadEvidence } from '../../lib/supabase'
import { IPLP_FRAMEWORK, ALL_CODES } from '../../lib/framework'
import { componentAsOf, componentFromStructure, versionMeta } from '../../lib/versions'
import { useFormVersion } from '../../hooks/useFormVersion'
import { useAuth } from '../../hooks/useAuth'
import { useToast } from '../../hooks/useToast'
import { useAppStore } from '../../store/appStore'
import { Icon, Modal, LevelBadge } from '../ui'
import type { Phase, Evidence, ComponentRating, FrameworkComponent } from '../../lib/types'

type RatingFields = {
  self_rating?: number | null
  admin_rating?: number | null
  self_reflection?: string | null
  evaluator_feedback?: string | null
}

interface ComponentEditorProps {
  iplpId: string
  componentCode: string
  phase: Phase
  isAdmin: boolean
  readOnly: boolean
  formVersion: number
  formTemplateId: string | null
}

const LEVEL_KEYS = ['unsatisfactory', 'basic', 'proficient', 'distinguished'] as const
const LEVEL_LABELS = ['Unsatisfactory', 'Basic', 'Proficient', 'Distinguished']

type AddEvidenceType = 'note' | 'link' | 'file' | 'photo'

interface AddEvidenceModalProps {
  type: AddEvidenceType
  iplpId: string
  componentCode: string
  districtId: string
  onClose: () => void
  onAdded: () => void
}

function AddEvidenceModal({ type, iplpId, componentCode, districtId, onClose, onAdded }: AddEvidenceModalProps) {
  const [val, setVal] = useState('')
  const [linkName, setLinkName] = useState('')
  const [dragging, setDragging] = useState(false)
  const [uploading, setUploading] = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)
  const toast = useToast()

  const cfg = {
    note: { icon: 'pen' as const, title: 'Write a note', hint: 'A typed narrative counts as evidence of your performance level.' },
    link: { icon: 'link' as const, title: 'Paste a link', hint: 'Google Docs, Sheets, Slides, GitHub, or any URL.' },
    file: { icon: 'upload' as const, title: 'Upload a file', hint: 'PDF, image, spreadsheet, video — drag in or browse.' },
    photo: { icon: 'camera' as const, title: 'Add a photo', hint: 'Take a photo now or choose from your photo library.' },
  }[type]

  async function handleNote() {
    if (!val.trim()) return
    const { error } = await supabase.from('evidence').insert({
      iplp_id: iplpId,
      district_id: districtId,
      component_code: componentCode,
      type: 'note',
      name: val.trim(),
    })
    if (error) { toast('Failed to add note'); return }
    toast('Note added')
    onAdded()
    onClose()
  }

  async function handleLink() {
    if (!val.trim()) return
    const { error } = await supabase.from('evidence').insert({
      iplp_id: iplpId,
      district_id: districtId,
      component_code: componentCode,
      type: 'link',
      name: linkName.trim() || val,
      url: val.trim(),
    })
    if (error) { toast('Failed to add link'); return }
    toast('Link added')
    onAdded()
    onClose()
  }

  async function handleFile(file: File) {
    setUploading(true)
    const result = await uploadEvidence(districtId, iplpId, componentCode, file)
    if (!result) { toast('Upload failed'); setUploading(false); return }
    const { error } = await supabase.from('evidence').insert({
      iplp_id: iplpId,
      district_id: districtId,
      component_code: componentCode,
      type,
      name: file.name,
      url: result.publicUrl,
      storage_path: result.path,
      mime_type: file.type,
      size_bytes: file.size,
    })
    setUploading(false)
    if (error) { toast('Failed to save evidence'); return }
    toast('Evidence uploaded')
    onAdded()
    onClose()
  }

  function onDrop(e: React.DragEvent) {
    e.preventDefault()
    setDragging(false)
    const file = e.dataTransfer.files[0]
    if (file) handleFile(file)
  }

  return (
    <Modal
      title={cfg.title}
      icon={cfg.icon}
      onClose={onClose}
      footer={
        type === 'link' ? (
          <button className="btn btn-pri" onClick={handleLink} disabled={!val.trim()}>Add link</button>
        ) : type === 'note' ? (
          <button className="btn btn-pri" onClick={handleNote} disabled={!val.trim()}>Add evidence</button>
        ) : null
      }
    >
      <p className="muted mb16" style={{ marginTop: 0, fontSize: 13.5 }}>{cfg.hint}</p>

      {type === 'note' ? (
        <textarea
          className="ta"
          autoFocus
          style={{ minHeight: 120 }}
          placeholder="Describe what you did, how students responded, engagement level…"
          value={val}
          onChange={(e) => setVal(e.target.value)}
        />
      ) : type === 'link' ? (
        <div className="col gap12">
          <input
            className="inp"
            type="url"
            placeholder="https://docs.google.com/…"
            value={val}
            onChange={(e) => setVal(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleLink()}
            autoFocus
          />
          <input
            className="inp"
            type="text"
            placeholder="Label (optional)"
            value={linkName}
            onChange={(e) => setLinkName(e.target.value)}
          />
        </div>
      ) : (
        <div
          className={`dropzone ${dragging ? 'dragging' : ''}`}
          style={{ minHeight: 120, flexDirection: 'column', gap: 16 }}
          onDragOver={(e) => { e.preventDefault(); setDragging(true) }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
        >
          <Icon name={cfg.icon} size={28} style={{ color: 'var(--ink-4)' }} />
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontWeight: 600, color: 'var(--ink-2)', marginBottom: 6 }}>
              {uploading ? 'Uploading…' : type === 'photo' ? 'Tap to use camera or choose from library' : 'Drag a file here, or click to browse'}
            </div>
            <div className="muted" style={{ fontSize: 13 }}>
              {type === 'photo' ? 'JPG, PNG, WEBP' : 'Any file up to 20 MB'}
            </div>
          </div>
          <input
            ref={fileRef}
            type="file"
            accept={type === 'photo' ? 'image/*' : undefined}
            style={{ display: 'none' }}
            onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f) }}
          />
          <button className="dz-btn" disabled={uploading} onClick={() => fileRef.current?.click()}>
            <Icon name="upload" size={14} />
            {uploading ? 'Uploading…' : type === 'photo' ? 'Open camera' : 'Browse files'}
          </button>
        </div>
      )}
    </Modal>
  )
}

export function ComponentEditor({ iplpId, componentCode, phase, isAdmin, readOnly, formVersion, formTemplateId }: ComponentEditorProps) {
  const { profile } = useAuth()
  const toast = useToast()
  const qc = useQueryClient()
  const [addEvType, setAddEvType] = useState<AddEvidenceType | null>(null)

  // Resolve the component as it read under the record's form version.
  // Precedence: the immutable published structure the plan was authored
  // under (district-customized forms) → the static Danielson version
  // diffs → the current Danielson catalogue.
  const { pinned, template } = useFormVersion(formTemplateId, formVersion)
  const baseDomain = IPLP_FRAMEWORK.find((d) => d.components.some((c) => c.code === componentCode))
  const baseComp = baseDomain?.components.find((c) => c.code === componentCode)
  const pinnedComp = componentFromStructure(pinned?.structure, componentCode)
  const comp: (FrameworkComponent & { _versioned?: boolean }) | undefined =
    pinnedComp ?? componentAsOf(componentCode, formVersion) ?? baseComp

  // What the district's *current* form says, for the "wording has since
  // changed" callout.
  const currentComp = pinnedComp
    ? componentFromStructure(template?.structure, componentCode) ?? baseComp
    : baseComp
  const titleChanged = !!currentComp && !!comp && currentComp.title !== comp.title
  const pinnedIsStale = !!pinnedComp && !!template && template.current_version > formVersion
  const showVersionNote = pinnedComp ? pinnedIsStale : !!comp?._versioned

  const { data: ratings = [] } = useQuery<ComponentRating[]>({
    queryKey: ['ratings', iplpId, componentCode],
    queryFn: async () => {
      const { data } = await supabase.from('component_ratings').select('*').eq('iplp_id', iplpId).eq('component_code', componentCode)
      return (data as ComponentRating[]) ?? []
    },
    enabled: !!iplpId && !!componentCode,
  })

  const { data: evidence = [] } = useQuery<Evidence[]>({
    queryKey: ['evidence', iplpId, componentCode],
    queryFn: async () => {
      const { data } = await supabase.from('evidence').select('*').eq('iplp_id', iplpId).eq('component_code', componentCode).order('created_at', { ascending: false })
      return (data as Evidence[]) ?? []
    },
    enabled: !!iplpId && !!componentCode,
  })

  const fallRating = ratings.find((r) => r.phase === 'fall')
  const phaseRating = ratings.find((r) => r.phase === phase)
  const currentRating = phase === 'spring' ? (phaseRating ?? fallRating) : fallRating
  const selfLevel = currentRating?.self_rating ?? null
  const adminLevel = phaseRating?.admin_rating ?? fallRating?.admin_rating ?? null

  const springLevel = phaseRating?.self_rating ?? null
  const fallLevel = fallRating?.self_rating ?? null
  const hasSpringDiff = phase === 'spring' && springLevel !== null && springLevel !== fallLevel

  // Reflection & feedback live on the canonical (fall) row, since they aren't phase-specific.
  const reflection = fallRating?.self_reflection ?? ''
  const feedback = fallRating?.evaluator_feedback ?? ''

  const upsertRating = useMutation({
    mutationFn: async ({ targetPhase, fields }: { targetPhase: Phase; fields: RatingFields }) => {
      const existing = ratings.find((r) => r.phase === targetPhase)
      if (existing) {
        await supabase.from('component_ratings').update(fields).eq('id', existing.id)
      } else {
        await supabase.from('component_ratings').insert({
          iplp_id: iplpId,
          district_id: profile?.district_id ?? '',
          component_code: componentCode,
          phase: targetPhase,
          ...fields,
        })
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ratings', iplpId, componentCode] })
      qc.invalidateQueries({ queryKey: ['ratings', iplpId] })
    },
  })

  const deleteEvidence = useMutation({
    mutationFn: async (evId: string) => {
      await supabase.from('evidence').delete().eq('id', evId)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['evidence', iplpId, componentCode] })
      qc.invalidateQueries({ queryKey: ['evidence', iplpId] })
      toast('Evidence removed')
    },
  })

  // Local, debounced state for the narrative boxes.
  const [reflDraft, setReflDraft] = useState(reflection)
  const [fbDraft, setFbDraft] = useState(feedback)
  useEffect(() => {
    setReflDraft(reflection)
    setFbDraft(feedback)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [componentCode, fallRating?.id])
  const reflTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const fbTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  function handleReflectionChange(v: string) {
    setReflDraft(v)
    if (reflTimer.current) clearTimeout(reflTimer.current)
    reflTimer.current = setTimeout(() => upsertRating.mutate({ targetPhase: 'fall', fields: { self_reflection: v } }), 700)
  }
  function handleFeedbackChange(v: string) {
    setFbDraft(v)
    if (fbTimer.current) clearTimeout(fbTimer.current)
    fbTimer.current = setTimeout(() => upsertRating.mutate({ targetPhase: 'fall', fields: { evaluator_feedback: v } }), 700)
  }

  function handleLevelPick(level: number) {
    if (readOnly || isAdmin) return
    upsertRating.mutate({ targetPhase: phase, fields: { self_rating: level } })
    toast(`Rated ${LEVEL_LABELS[level - 1]}`)
  }

  function handleAdminRating(level: number) {
    if (!isAdmin || readOnly) return
    const next = level === adminLevel ? null : level
    upsertRating.mutate({ targetPhase: phase, fields: { admin_rating: next } })
    toast(next === null ? 'Admin rating cleared' : `Admin rated ${LEVEL_LABELS[level - 1]}`)
  }

  const codeIdx = ALL_CODES.indexOf(componentCode)
  const prevCode = codeIdx > 0 ? ALL_CODES[codeIdx - 1] : null
  const nextCode = codeIdx < ALL_CODES.length - 1 ? ALL_CODES[codeIdx + 1] : null

  function navToCode(code: string) {
    const d = IPLP_FRAMEWORK.find((dom) => dom.components.some((c) => c.code === code))
    if (!d) return
    useAppStore.getState().setNav({ page: 'domain', domain: d.n, code })
  }

  function evIcon(type: string) {
    return type === 'note' ? 'pen' : type === 'link' ? 'link' : type === 'photo' ? 'camera' : 'doc'
  }

  if (!comp || !baseDomain) {
    return (
      <div className="main">
        <div className="page-w">
          <p className="muted">Component not found: {componentCode}</p>
        </div>
      </div>
    )
  }

  const vmeta = versionMeta(formVersion)

  return (
    <div className="main">
      <div className="page-w">
        {/* Header */}
        <div className="row center gap8 mb4">
          <span className="eyebrow">
            Domain {baseDomain.n} · {baseDomain.title}
          </span>
        </div>
        <div className="row" style={{ alignItems: 'flex-start', gap: 18, marginBottom: 14 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h1 className="serif" style={{ margin: 0, fontWeight: 500, fontSize: 26, letterSpacing: '-.01em', lineHeight: 1.2 }}>
              <span className="mono" style={{ fontSize: 18, fontWeight: 600, color: 'var(--navy)', marginRight: 10 }}>
                {comp.code}
              </span>
              {comp.title}
            </h1>

            {/* Criteria assessed */}
            <div className="crit-block">
              <div className="crit-label">Criteria assessed in this component</div>
              <ol className="crit-list">
                {comp.elements.map((e, i) => (
                  <li key={i}>
                    <span className="crit-num mono">{i + 1}</span>
                    <span>{e}</span>
                  </li>
                ))}
              </ol>
            </div>

            {/* Version note */}
            {showVersionNote && (
              <div className="ver-note">
                <Icon name="clock" size={14} style={{ flex: '0 0 auto', marginTop: 1 }} />
                <div>
                  {pinnedComp ? (
                    <>
                      Shown as written in <b>v{formVersion}</b> of this form
                      {pinned?.published_at && (
                        <>
                          {' '}(published{' '}
                          {new Date(pinned.published_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })})
                        </>
                      )}.
                    </>
                  ) : (
                    <>
                      Shown as written in the <b>{vmeta.effective}</b> form ({vmeta.label}).
                    </>
                  )}
                  {titleChanged && (
                    <>
                      {' '}
                      The current form reads “<b>{currentComp?.title}</b>.”
                    </>
                  )}{' '}
                  Responses always display the wording from when they were entered.
                </div>
              </div>
            )}
          </div>

          <div className="ph-r row gap10" style={{ flex: '0 0 auto', marginLeft: 0 }}>
            {selfLevel && <LevelBadge level={selfLevel} />}
          </div>
        </div>

        {/* Spring protocol note */}
        {phase === 'spring' && !readOnly && (
          <div
            className="row center gap8 mb16"
            style={{ fontSize: 13, color: 'var(--ink-2)', background: 'var(--lvl3-bg)', padding: '10px 14px', borderRadius: 10, border: '1px solid var(--lvl3-line)' }}
          >
            <Icon name="sparkle" size={16} style={{ color: 'var(--lvl3)' }} />
            <span>
              Spring protocol: if your level has grown since fall, select the new level — it will be{' '}
              <b style={{ color: '#2f6243' }}>highlighted green</b>. Your fall selection stays for comparison.
            </span>
          </div>
        )}

        {/* 4-level descriptor grid */}
        <div className="lvlgrid">
          {LEVEL_KEYS.map((key, idx) => {
            const levelNum = idx + 1
            const isSelected = selfLevel === levelNum
            const isSpringHighlight = hasSpringDiff && springLevel === levelNum
            let cls = 'lvlcol'
            if (isSelected) cls += ` sel sel-${levelNum}`
            if (isSpringHighlight) cls += ' spring'

            return (
              <button
                key={key}
                className={cls}
                onClick={() => handleLevelPick(levelNum)}
                disabled={readOnly || isAdmin}
                aria-pressed={isSelected}
                style={{ cursor: readOnly || isAdmin ? 'default' : 'pointer' }}
              >
                {isSpringHighlight && <span className="spring-tag">SPRING ▲</span>}
                <div className="lvlhead">
                  <span className="num">{levelNum}</span>
                  <span className="nm">{LEVEL_LABELS[idx]}</span>
                </div>
                <div className="lvlbody">{comp.levels[key]}</div>
                <div className="lvlpick">
                  {isSelected ? (
                    <>
                      <Icon name="check" size={12} /> Selected
                    </>
                  ) : (
                    <>Select this level</>
                  )}
                </div>
              </button>
            )
          })}
        </div>

        {/* Self-reflection */}
        <div className="mt24 refl-box">
          <div className="row center gap8 mb8">
            <span className="sec-title">Self-Reflection</span>
            <span className="muted" style={{ fontSize: 12.5 }}>
              {isAdmin ? 'Written by the staff member' : 'Why did you select this level? What does your practice look like here?'}
            </span>
          </div>
          <textarea
            className="ta"
            style={{ minHeight: 90 }}
            readOnly={isAdmin || readOnly}
            aria-label="Self-reflection"
            placeholder={isAdmin ? 'No self-reflection written for this component.' : 'Reflect on your present level of performance for this component…'}
            value={reflDraft}
            onChange={(e) => handleReflectionChange(e.target.value)}
          />
        </div>

        {/* Admin rating row */}
        {isAdmin && (
          <div className="card card-pad mt24" style={{ background: 'var(--navy-tint)', border: '1px solid var(--navy-tint-2)' }}>
            <div className="row between gap12">
              <div>
                <div className="sec-title" style={{ color: 'var(--navy)' }}>Evaluator Score</div>
                <div style={{ fontSize: 13, color: 'var(--ink-3)', marginTop: 3 }}>
                  Record your evaluator score for this component
                </div>
              </div>
              <div className="row gap8">
                {[1, 2, 3, 4].map((lvl) => (
                  <button
                    key={lvl}
                    className={`badge-lvl ${adminLevel === lvl ? `lvl${lvl}` : 'lvl0'}`}
                    style={{ cursor: readOnly ? 'default' : 'pointer', width: 40, justifyContent: 'center' }}
                    disabled={readOnly}
                    aria-pressed={adminLevel === lvl}
                    aria-label={`Evaluator score ${lvl}`}
                    onClick={() => handleAdminRating(lvl)}
                  >
                    {lvl}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Evidence */}
        <div className="mt24">
          <div className="evbox">
            <div className="row center between mb12">
              <div className="row center gap8">
                <span className="sec-title">Evidence &amp; Artifacts</span>
                {(phase === 'spring' ? springLevel ?? fallLevel : fallLevel) === 4 && evidence.length === 0 && (
                  <span className="chip chip-warn">
                    <Icon name="warn" size={12} /> Required for Distinguished
                  </span>
                )}
                {evidence.length > 0 && (
                  <span className="chip chip-ok">
                    <Icon name="check" size={12} /> {evidence.length} attached
                  </span>
                )}
              </div>
            </div>
            <div className="muted" style={{ fontSize: 12.5, marginTop: -6, marginBottom: 12, lineHeight: 1.45 }}>
              A typed note counts as evidence — describe what you did, how students responded, engagement, etc. Links, files,
              and photos are artifacts.
            </div>

            {evidence.length > 0 && (
              <div className="mb12">
                {evidence.map((ev) => (
                  <div key={ev.id} className={`evitem${ev.type === 'note' ? ' evnote' : ''}`}>
                    <div className={`ic ${ev.type}`}>
                      <Icon name={evIcon(ev.type)} size={17} />
                    </div>
                    <div className="meta">
                      <div className={`t${ev.type === 'note' ? ' note-t' : ''}`}>{ev.name}</div>
                      <div className="s">
                        {ev.type === 'note' ? 'Typed note' : ev.type.toUpperCase()}
                        {ev.size_bytes ? ` · ${Math.round(ev.size_bytes / 1024)} KB` : ''}
                      </div>
                    </div>
                    {ev.url && (
                      <a href={ev.url} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-ghost" style={{ width: 30, padding: 0 }}>
                        <Icon name="externalLink" size={13} />
                      </a>
                    )}
                    {!readOnly && (
                      <button className="btn btn-ghost btn-sm" style={{ width: 30, padding: 0, color: 'var(--bad)' }} onClick={() => deleteEvidence.mutate(ev.id)}>
                        <Icon name="x" size={15} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}

            {!readOnly && (
              <div className="dropzone">
                <span className="muted" style={{ fontSize: 13, marginRight: 4 }}>Add evidence:</span>
                <button className="dz-btn" onClick={() => setAddEvType('note')}><Icon name="pen" size={15} /> Write a note</button>
                <button className="dz-btn" onClick={() => setAddEvType('link')}><Icon name="link" size={15} /> Paste a link</button>
                <button className="dz-btn" onClick={() => setAddEvType('file')}><Icon name="upload" size={15} /> Upload file</button>
                <button className="dz-btn" onClick={() => setAddEvType('photo')}><Icon name="camera" size={15} /> Take / add photo</button>
              </div>
            )}
            {readOnly && evidence.length === 0 && (
              <div className="muted" style={{ fontSize: 13, fontStyle: 'italic' }}>No evidence attached.</div>
            )}
          </div>
        </div>

        {/* Evaluator feedback */}
        {(isAdmin || feedback) && (
          <div className="mt24 fb-box">
            <div className="row center gap8 mb8">
              <Icon name="pen" size={16} style={{ color: 'var(--navy)' }} />
              <span className="sec-title">Evaluator feedback</span>
              {!isAdmin && <span className="chip chip-info" style={{ height: 20, fontSize: 11 }}>From your evaluator</span>}
            </div>
            <textarea
              className="ta"
              style={{ minHeight: 74 }}
              readOnly={!isAdmin || readOnly}
              placeholder={isAdmin ? 'Leave feedback on this component for the staff member…' : ''}
              value={fbDraft}
              onChange={(e) => handleFeedbackChange(e.target.value)}
            />
          </div>
        )}

        {/* Prev/Next navigation */}
        <div className="row between mt32" style={{ paddingTop: 8, borderTop: '1px solid var(--line)' }}>
          {prevCode ? (
            <button className="btn" onClick={() => navToCode(prevCode)}>
              <Icon name="arrowL" size={15} />
              {prevCode.toUpperCase()}
            </button>
          ) : (
            <button className="btn" disabled title="This is the first component">
              <Icon name="arrowL" size={15} /> Previous
            </button>
          )}
          {nextCode ? (
            <button className="btn btn-pri" onClick={() => navToCode(nextCode)}>
              {nextCode.toUpperCase()}
              <Icon name="arrowR" size={15} />
            </button>
          ) : (
            <button className="btn btn-pri" disabled title="This is the last component">
              Next <Icon name="arrowR" size={15} />
            </button>
          )}
        </div>
      </div>

      {/* Add evidence modal */}
      {addEvType && profile && (
        <AddEvidenceModal
          type={addEvType}
          iplpId={iplpId}
          componentCode={componentCode}
          districtId={profile.district_id}
          onClose={() => setAddEvType(null)}
          onAdded={() => {
            qc.invalidateQueries({ queryKey: ['evidence', iplpId, componentCode] })
            qc.invalidateQueries({ queryKey: ['evidence', iplpId] })
          }}
        />
      )}
    </div>
  )
}
